import { Component } from '@theme/component';
import { CartAddEvent, CartUpdateEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';

const SIZE_STORAGE_KEY = 'apc-cart-addon-sizes';
const MODIFIER_KEYS = new Set(['Alt', 'CapsLock', 'Control', 'Meta', 'Shift']);

/**
 * Cart add-ons (LaDress: shapewear): adds rows' variants, strips add-on lines below the threshold; the server decides which.
 * @extends {Component}
 */
class ApcCartAddons extends Component {
  static observedAttributes = ['data-remove-keys'];

  #removing = false;

  /** @type {MutationObserver|undefined} */
  #observer;

  connectedCallback() {
    super.connectedCallback();
    restoreSizes(this);

    // Cart re-renders morph the rows back to server defaults, so re-apply remembered sizes after each one.
    this.#observer = new MutationObserver(() => restoreSizes(this));
    this.#observer.observe(this, { childList: true, subtree: true, attributes: true, attributeFilter: ['selected'] });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#observer?.disconnect();
  }

  /**
   * Strips add-on lines when the server says the cart no longer qualifies.
   * Fires on upgrade too, so a locked cart is cleaned up on first paint.
   * @param {string} name
   * @param {string|null} oldValue
   * @param {string|null} newValue
   */
  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'data-remove-keys' && newValue && !this.#removing) {
      this.#removeLines(newValue.split(','));
    }
  }

  /**
   * Section ids of every cart-items-component, so an add returns their rendered
   * HTML in the same round-trip and the drawer can morph in place.
   * @returns {string} Comma-separated section ids, or '' if none.
   */
  #cartSectionIds() {
    const ids = [];
    for (const item of document.querySelectorAll('cart-items-component')) {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        ids.push(item.dataset.sectionId);
      }
    }
    return ids.join(',');
  }

  /**
   * Removes add-on lines from the cart and announces the new cart state.
   * @param {string[]} keys - Line item keys to remove
   */
  async #removeLines(keys) {
    if (!keys.length) return;
    this.#removing = true;

    try {
      /** @type {Record<string, number>} */
      const updates = {};
      for (const key of keys) {
        updates[key] = 0;
      }

      const response = await fetch(Theme.routes.cart_update_url, {
        ...fetchConfig('json', {
          body: JSON.stringify({ updates }),
        }),
      });

      if (!response.ok) {
        console.error('[apc-cart-addons] Failed to remove add-on', response.status);
        return;
      }

      const cart = await response.json();

      document.dispatchEvent(
        new CartUpdateEvent(cart, 'apc-cart-addons', {
          source: 'apc-cart-addons',
          itemCount: cart.item_count,
        }),
      );
    } catch (error) {
      console.error('[apc-cart-addons]', error);
    } finally {
      this.#removing = false;
    }
  }

  /**
   * Adds an add-on variant to the cart.
   *
   * The `_apc_addon` line property marks this line as one the list added, keeping
   * it distinguishable from the same product added elsewhere. The gate reclaims
   * gated lines by collection either way, so this is for traceability in the cart
   * and order data, not for the gate itself.
   *
   * @param {string} variantId - The variant ID to add
   * @returns {Promise<boolean>} Whether the add succeeded
   */
  async #addToCart(variantId) {
    const sections = this.#cartSectionIds();
    const body = JSON.stringify({
      items: [{ id: Number(variantId), quantity: 1, properties: { _apc_addon: 'true' } }],
      sections: sections || undefined,
      sections_url: window.location.pathname,
    });

    const response = await fetch(Theme.routes.cart_add_url, {
      ...fetchConfig('json', { body }),
    });

    const result = await response.json();

    // Shopify flags add failures via a `status` field, even on a 200.
    if (!response.ok || result.status) {
      console.error('[apc-cart-addons] Failed to add add-on', result.message || response.status);
      return false;
    }

    // `/cart/add.js` returns only the added lines, so the cart's new total has to
    // be read separately: cart-icon treats `itemCount` as the absolute count for
    // every source except the product form, and a wrong value pins the bag badge.
    let itemCount;
    try {
      const cartResponse = await fetch(`${Theme.routes.cart_url}.js`);
      if (cartResponse.ok) itemCount = (await cartResponse.json()).item_count;
    } catch (error) {
      console.error('[apc-cart-addons] Could not read cart count', error);
    }

    // cart-icon treats a missing count as 0 and *sets* rather than increments, so
    // a failed read would blank the bag badge on a successful add. Fall back to
    // the count it last stored, plus this line.
    if (typeof itemCount !== 'number') {
      try {
        const stored = JSON.parse(sessionStorage.getItem('cart-count') || 'null');
        const previous = Number(stored?.value);
        if (Number.isFinite(previous)) itemCount = previous + 1;
      } catch {
        // Leave undefined; a stale badge is better than a wrong one.
      }
    }

    document.dispatchEvent(
      new CartAddEvent(result, 'apc-cart-addons', {
        source: 'apc-cart-addons',
        itemCount,
        sections: result.sections,
      }),
    );

    return true;
  }

  /**
   * Adds the variant a button stands for; syncItem keeps it in step with the row's colour and size.
   * @param {HTMLButtonElement} button - The clicked add button
   */
  async addAddon(button) {
    if (button.disabled) return;

    const variantId = button.dataset.addonVariantId;

    if (!variantId) return;

    button.disabled = true;

    let added = false;
    try {
      added = await this.#addToCart(variantId);
    } catch (error) {
      console.error('[apc-cart-addons]', error);
    }

    // A successful add re-renders the cart and drops this row, so only a failure
    // needs the button handed back to the customer.
    if (!added) button.disabled = false;
  }
}

if (!customElements.get('apc-cart-addons')) {
  customElements.define('apc-cart-addons', ApcCartAddons);
}

/**
 * Delegated at document level so add-on rows keep working across the DOM
 * morphing the cart drawer does on every update.
 */
document.addEventListener('click', (event) => {
  const target = /** @type {HTMLElement} */ (event.target);

  /** @type {HTMLButtonElement|null} */
  const button = target.closest('apc-cart-addons [data-addon-variant-id]');
  if (!button) return;

  /** @type {ApcCartAddons|null} */
  const component = button.closest('apc-cart-addons');
  component?.addAddon(button);
});

/**
 * Swaps colour on a swatch pick, and remembers the size a customer picks.
 */
document.addEventListener('change', (event) => {
  const target = /** @type {HTMLElement} */ (event.target);
  const item = target.closest('[data-addon-item]');
  if (!(item instanceof HTMLElement)) return;

  if (target.closest('[data-addon-colours]')) {
    switchColour(item);
    return;
  }

  if (target instanceof HTMLSelectElement && target.matches('[data-addon-variant-select]')) {
    const label = target.selectedOptions[0]?.textContent?.trim();
    if (label && item.dataset.addonStyle) {
      storeSize(item.dataset.addonStyle, label);
      restoreSize(item, label);
    }
    syncItem(item);
  }
});

document.addEventListener(
  'pointerdown',
  (event) => {
    if (event.target instanceof Element && event.target.closest('apc-cart-addons')) {
      document.documentElement.setAttribute('data-apc-addons-pointer', '');
    }
  },
  true,
);
document.addEventListener(
  'keydown',
  (event) => {
    if (event.metaKey || event.ctrlKey || MODIFIER_KEYS.has(event.key)) return;
    document.documentElement.removeAttribute('data-apc-addons-pointer');
  },
  true,
);

/**
 * Reads the remembered size label per add-on style.
 * @returns {Record<string, string>}
 */
function readSizes() {
  try {
    return JSON.parse(localStorage.getItem(SIZE_STORAGE_KEY) || '{}') || {};
  } catch {
    return {};
  }
}

/**
 * Remembers the size label picked for an add-on style.
 * @param {string} style - The item's `data-addon-style` key
 * @param {string} label - The size option's label, e.g. "M"
 */
function storeSize(style, label) {
  try {
    localStorage.setItem(SIZE_STORAGE_KEY, JSON.stringify({ ...readSizes(), [style]: label }));
  } catch {
    // Storage blocked: the pick still applies, it just isn't remembered.
  }
}

/**
 * Selects a size label in every colour's size select of an item, where that size is available.
 * @param {HTMLElement} item
 * @param {string} label
 * @returns {boolean} Whether any select changed
 */
function restoreSize(item, label) {
  let changed = false;
  for (const select of item.querySelectorAll('[data-addon-variant-select]')) {
    if (!(select instanceof HTMLSelectElement)) continue;
    const option = Array.from(select.options).find((candidate) => candidate.textContent?.trim() === label);
    if (option && !option.selected) {
      option.selected = true;
      changed = true;
    }
  }
  return changed;
}

/**
 * Applies remembered sizes to every add-on item under a root.
 * @param {ParentNode} root
 */
function restoreSizes(root) {
  const sizes = readSizes();
  for (const item of root.querySelectorAll('[data-addon-item][data-addon-style]')) {
    if (!(item instanceof HTMLElement)) continue;
    const label = sizes[item.dataset.addonStyle || ''];
    if (label && restoreSize(item, label)) syncItem(item);
  }
}

/**
 * Shows the checked colour sibling's image and size select, then syncs title, price and variant.
 * @param {HTMLElement} item
 */
function switchColour(item) {
  const input = /** @type {HTMLInputElement|null} */ (item.querySelector('[data-addon-colours] input:checked'));
  if (!input) return;

  for (const element of item.querySelectorAll('[data-addon-member]')) {
    if (!(element instanceof HTMLElement)) continue;
    const inactive = element.dataset.addonMember !== input.value;
    if (element instanceof HTMLImageElement) element.toggleAttribute('data-addon-inactive', inactive);
    else element.hidden = inactive;
  }

  const title = item.querySelector('[data-addon-title-display]');
  if (title && input.dataset.addonTitle) title.textContent = input.dataset.addonTitle;

  const colourName = item.querySelector('[data-addon-colour-name]');
  if (colourName && input.dataset.addonColour) colourName.textContent = input.dataset.addonColour;

  syncItem(item);
}

/**
 * Points the Add button and price at the visible size select, or the checked colour's default variant.
 * @param {HTMLElement} item
 */
function syncItem(item) {
  const colour = /** @type {HTMLInputElement|null} */ (item.querySelector('[data-addon-colours] input:checked'));
  const sizeSelect = /** @type {HTMLSelectElement|null} */ (
    item.querySelector('[data-addon-member]:not([hidden]) [data-addon-variant-select]')
  );
  const sizeOption = sizeSelect?.selectedOptions[0];

  const button = /** @type {HTMLButtonElement|null} */ (item.querySelector('button[data-addon-variant-id]'));
  const variantId = sizeOption?.value || colour?.dataset.addonDefaultVariant;
  if (button && variantId) button.dataset.addonVariantId = variantId;

  const price = sizeOption?.dataset.addonPrice || colour?.dataset.addonPrice;
  const display = item.querySelector('[data-addon-price-display]');
  if (price && display) display.textContent = price;
}
